import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import i18n from "../../../config/languageInit";
import { authClient } from "../services/authClient";

const name = "auth";
const initialState = createInitialState();

export const loginWithAad = createAsyncThunk(
  `${name}/log-in-aad`,
  ({ email, token }) =>
    authClient.post(`azure-login`, {
      email: email,
      token: token,
    }),
);

export const refreshJwtByAad = createAsyncThunk(
  `${name}/refreshJwt`,
  ({ email, token }) =>
    authClient.post(`azure-login`, {
      email: email,
      token: token,
    }),
);

export const loginWithCredentials = createAsyncThunk(
  `${name}/login`,
  ({ email, password, mfaMethod }) =>
    authClient.post(`login`, {
      email,
      password,
      mfaMethod,
    }),
);

export const verifyMfaCode = createAsyncThunk(
  `${name}/verify-mfa`,
  ({ code, method }, { getState }) => {
    const { pendingUserId } = getState().auth;
    if (method === "authenticator") {
      return authClient.post("verify-totp", { userId: pendingUserId, code });
    }
    return authClient.post("verify-email", { email: pendingUserId, code });
  },
);

export const forgotPassword = createAsyncThunk(
  `${name}/forgot-password`,
  ({ email }) => authClient.post("forgot-password", { email }),
);

export const verifyResetCode = createAsyncThunk(
  `${name}/verify-reset-code`,
  ({ email, code }) => authClient.post("verify-email", { email, code }),
);

export const resetPassword = createAsyncThunk(
  `${name}/reset-password`,
  ({ email, password }) =>
    authClient.post("reset-password", { email, password }),
);

export const register = createAsyncThunk(
  `${name}/register`,
  ({ email, firstName, lastName }) =>
    authClient.post("register", {
      EMAIL: email,
      FIRST_NAME: firstName,
      LAST_NAME: lastName,
    }),
);

export const verifyRegistration = createAsyncThunk(
  `${name}/verify-registration`,
  ({ email, code, password }) =>
    authClient.post("complete-registration", {
      EMAIL: email,
      CODE: code,
      PASSWORD_HASH: password,
    }),
);

// Thunk for offline login — side-effects (localStorage) belong here, not in reducer
export const offlineLogin = createAsyncThunk(
  `${name}/offline-login`,
  ({ email }) => {
    const fakeToken = btoa(
      JSON.stringify({
        email: email,
        given_name: "Super Admin",
        nameid: "offline-super-user",
        aud: "DAIKIN",
        iss: "DTAS.LOCAL",
        exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60,
        nbf: Math.floor(Date.now() / 1000),
      }),
    );
    localStorage.setItem("jwtToken", fakeToken);
    return { email, fakeToken };
  },
);

// Thunk for logout — side-effects (localStorage, navigation) belong here
export const performLogout = createAsyncThunk(`${name}/logout`, async () => {
  // Notify backend to deactivate push tokens for this session
  try {
    await authClient.post("logout", {});
  } catch {
    // Ignore errors (e.g. expired token) — proceed with client-side cleanup
  }
  localStorage.removeItem("jwtToken");
  localStorage.removeItem("aadToken");
  window.location.href = window.location.origin + "/";
});

const slice = createSlice({
  name,
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.isAuthenticated = false;
    },

    changeMfa(state) {
      state.isAuthenticated = false;
      state.isMfa = false;
      state.error = null;
    },

    resetLogin() {
      localStorage.removeItem("jwtToken");
    },

    setLoading(state, action) {
      state.isLoading = action.payload;
      if (!action.payload) {
        state.error = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(refreshJwtByAad.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(refreshJwtByAad.fulfilled, (state, action) => {
      if (action.payload?.token) {
        localStorage.setItem("jwtToken", action.payload.token);
        try {
          state.user = jwtDecode(action.payload.token);
        } catch (err) {
          console.error("JWT decode error:", err);
        }
      }
      state.isAuthenticated = true;
      state.isLoading = false;
    });
    builder.addCase(refreshJwtByAad.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error;
    });

    builder.addCase(loginWithAad.pending, (state) => {
      state.error = null;
      state.isMfa = false;
      state.isLoading = true;
    });
    builder.addCase(loginWithAad.fulfilled, (state, action) => {
      const azureToken = action.meta.arg.token;
      if (action.payload && azureToken) {
        localStorage.setItem("aadToken", azureToken);
        localStorage.setItem("jwtToken", action.payload.token);
        try {
          state.user = jwtDecode(action.payload.token);
        } catch (err) {
          console.error("JWT decode error:", err);
        }
      }
      state.isMfa = false;
      state.isAuthenticated = true;
      state.isLoading = false;
    });
    builder.addCase(loginWithAad.rejected, (state, action) => {
      if (action.error.name === "TypeError") {
        state.error = {
          message: i18n.t("systemError"),
        };
      } else {
        state.error = action.error;
      }
      state.isLoading = false;
      state.isMfa = false;
    });

    builder.addCase(loginWithCredentials.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(loginWithCredentials.fulfilled, (state, action) => {
      const data = action.payload;
      // NOTE: MFA is not yet implemented on the backend. This code is kept for future use.
      if (data?.requiresMfa) {
        state.isMfa = true;
        state.mfaMethod = action.meta.arg.mfaMethod || "email";
        state.pendingUserId = data.userId || null;
        state.isLoading = false;
      } else if (data?.token) {
        localStorage.setItem("jwtToken", data.token);
        try {
          state.user = jwtDecode(data.token);
        } catch (err) {
          console.error("JWT decode error:", err);
        }
        state.isAuthenticated = true;
        state.mfaMethod = null;
        state.pendingUserId = null;
        state.isLoading = false;
      } else {
        state.isLoading = false;
      }
    });
    builder.addCase(loginWithCredentials.rejected, (state, action) => {
      if (action.error.name === "TypeError") {
        state.error = {
          message: i18n.t("systemError"),
        };
      } else {
        state.error = action.error;
      }
      state.isLoading = false;
    });

    builder.addCase(verifyMfaCode.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(verifyMfaCode.fulfilled, (state, action) => {
      const data = action.payload;
      if (data?.token) {
        localStorage.setItem("jwtToken", data.token);
        try {
          state.user = jwtDecode(data.token);
        } catch (err) {
          console.error("JWT decode error:", err);
        }
      }
      state.isMfa = false;
      state.mfaMethod = null;
      state.pendingUserId = null;
      state.isAuthenticated = true;
      state.isLoading = false;
    });
    builder.addCase(verifyMfaCode.rejected, (state, action) => {
      state.error = action.error;
      state.isLoading = false;
    });

    // offlineLogin thunk
    builder.addCase(offlineLogin.fulfilled, (state, action) => {
      const { email } = action.payload;
      state.user = {
        email,
        given_name: "Super Admin",
        nameid: "offline-super-user",
        exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60,
      };
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
    });

    // performLogout thunk
    builder.addCase(performLogout.pending, (state) => {
      state.user = null;
      state.isAuthenticated = false;
    });
  },
});
/**
 * JWT Token Payload Type Definition
 * @typedef {Object} JWTTokenPayload
 * @property {string} aud - Audience - JWT'nin hedef kitlesi (örn: "DAIKIN")
 * @property {string} email - Kullanıcının email adresi
 * @property {number} exp - Expiration Time - Token'ın sona erme zamanı (Unix timestamp)
 * @property {string} given_name - Kullanıcının tam adı
 * @property {string} iss - Issuer - JWT'yi yayınlayan taraf (örn: "DTAS.ALL")
 * @property {string} nameid - Kullanıcının benzersiz UUID'si
 * @property {number} nbf - Not Before - Token'ın geçerli olmaya başlayacağı zaman (Unix timestamp)
 * @property {string} token - Token string'i (isteğe bağlı, payload'da bulunmayabilir)
 */
// Exports
export const authActions = { ...slice.actions, offlineLogin, performLogout };
export const authReducer = slice.reducer;

// Implementation
function createInitialState() {
  /**
   * @type {JWTTokenPayload}
   */
  let user = localStorage.getItem("jwtToken");
  const token = user;

  if (user) {
    try {
      // Try JWT decode first
      user = jwtDecode(user);

      // Check issuer for JWT tokens
      if (user.iss && user.iss !== "DTAS.ALL" && user.iss !== "DTAS.LOCAL") {
        localStorage.removeItem("jwtToken");
        user = null;
      } else {
        const tokenDate = new Date(user.exp * 1000);
        if (tokenDate <= new Date()) {
          localStorage.removeItem("jwtToken");
          user = null;
        } else {
          user.token = token;
        }
      }
    } catch (jwtError) {
      // Not a JWT, try as offline token (base64 JSON)
      try {
        user = JSON.parse(atob(user));
        const tokenDate = new Date(user.exp * 1000);
        if (tokenDate <= new Date()) {
          localStorage.removeItem("jwtToken");
          user = null;
        } else {
          user.token = token;
        }
      } catch (offlineError) {
        // Invalid token format
        localStorage.removeItem("jwtToken");
        user = null;
      }
    }
  }

  return {
    user: user || null,
    error: null,
    isMfa: false,
    mfaMethod: null,
    pendingUserId: null,
    isAuthenticated: !!user,
    isLoading: false,
    roles: [],
  };
}
